
import { useState, Suspense } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Button, Card, Input } from '@/components/ui';
import { api } from '@/lib/api';

function AcceptInviteForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!token) {
    return (
      <Card variant="elevated" padding="lg" className="text-center space-y-4">
        <p className="text-slate-700 dark:text-slate-300">
          Invalid or missing invite link. Please use the link from your invite email, or ask an admin to send a new invite.
        </p>
        <Link to="/login">
          <Button variant="outline">Go to Login</Button>
        </Link>
      </Card>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    const response = await api.post<{ message: string }>(
      '/auth/accept-invite',
      { token, password },
      { auth: false }
    );
    if (response.error) {
      setError(response.error);
      setIsSubmitting(false);
      return;
    }
    setSuccess(true);
    setIsSubmitting(false);
  };

  if (success) {
    return (
      <Card variant="elevated" padding="lg" className="text-center space-y-4">
        <p className="text-slate-700 dark:text-slate-300">
          Your password has been set. You can now log in with your email and new password.
        </p>
        <Button onClick={() => navigate('/login')} fullWidth>
          Go to Login
        </Button>
      </Card>
    );
  }

  return (
    <Card variant="elevated" padding="none" className="p-6 sm:p-8 space-y-5">
      <form onSubmit={handleSubmit} className="space-y-5">
      <p className="text-slate-600 dark:text-slate-400 text-sm">
        Set a password for your account. Use at least 8 characters.
      </p>
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}
      <Input
        label="New Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        minLength={8}
        autoComplete="new-password"
        disabled={isSubmitting}
        placeholder="At least 8 characters"
      />
      <Input
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        minLength={8}
        autoComplete="new-password"
        disabled={isSubmitting}
        placeholder="Repeat password"
      />
      <Button
        type="submit"
        fullWidth
        isLoading={isSubmitting}
        disabled={isSubmitting || !password || !confirmPassword}
      >
        Set Password
      </Button>
    </form>
    </Card>
  );
}

export default function AcceptInvitePage() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Set Your Password
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            You were invited to join. Create a password to activate your account.
          </p>
        </div>
        <Suspense
          fallback={
            <Card variant="elevated" padding="lg" className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </Card>
          }
        >
          <AcceptInviteForm />
        </Suspense>
        <p className="text-center mt-6 text-sm text-slate-600 dark:text-slate-400">
          <Link to="/login" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            Back to login
          </Link>
        </p>
      </div>
    </main>
  );
}
